import { Route } from '@angular/router';
import { CreateAccountComponent } from './pages/create-account/create-account.component';
import { guestGuard, authGuard, questionnaireGuard, questionnaireCompletedGuard, tfaAuthGuard, tfaGuard, tfaSetupGuard } from '@trading-fe/guards';
import { langGuard, langRedirectGuard } from './guards/lang.guard';
import { serviceUnavailableGuard } from './guards/service-unavailable.guard';

const langChildRoutes: Route[] = [
  {
    path: 'sign-in',
    loadComponent: () =>
      import('./pages/sign-in/sign-in.component').then(
        (m) => m.SignInComponent
      ),
    canActivate: [guestGuard],
    title: 'Cube | Sign In',
  },
  {
    path: 'create-account',
    component: CreateAccountComponent,
    canActivate: [guestGuard],
    title: 'Cube | Create Account',
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password.component').then(
        (m) => m.ForgotPasswordComponent
      ),
    canActivate: [guestGuard],
    title: 'Cube | Forgot Password',
  },
  {
    path: 'after-signup',
    loadComponent: () =>
      import('./pages/after-signup/after-signup.component').then(
        (m) => m.AfterSignupComponent
      ),
    canActivate: [guestGuard],
    title: 'Cube | After Signup',
  },
  {
    path: 'questionnaire',
    loadComponent: () =>
      import('./pages/questionnaire/questionnaire.component').then(
        (m) => m.QuestionnaireComponent
      ),
    canActivate: [authGuard, tfaGuard, tfaSetupGuard, questionnaireCompletedGuard],
    title: 'Cube | Questionnaire',
  },
  {
    path: 'setup-tfa',
    loadComponent: () =>
      import('./pages/setup-tfa/setup-tfa.component').then(
        (m) => m.SetupTfaComponent
      ),
    canActivate: [authGuard, tfaGuard],
    title: 'Cube | Setup 2FA',
  },
  {
    path: 'tfa-verification',
    loadComponent: () =>
      import('./pages/tfa-verification/tfa-verification.component').then(
        (m) => m.TfaVerificationComponent
      ),
    canActivate: [tfaAuthGuard],
    title: 'Cube | 2FA Verification',
  },
  {
    path: '',
    loadComponent: () =>
      import('./components/main-layout/main-layout.component').then(
        (m) => m.MainLayoutComponent
      ),
    canActivate: [authGuard, tfaGuard, tfaSetupGuard, questionnaireGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/home/home.component').then(
            (m) => m.HomeComponent
          ),
        pathMatch: 'full',
        title: 'Cube | Home',
      },
      {
        path: 'buy-sell/:tickerId/:sourceId',
        loadComponent: () =>
          import('./pages/buy-sell/buy-sell.component').then(
            (m) => m.BuySellComponent
          ),
        title: 'Cube | Buy/Sell',
      },
      {
        path: 'buy-sell',
        loadComponent: () =>
          import('./pages/buy-sell-landing/buy-sell-landing.component').then(
            (m) => m.BuySellLandingComponent
          ),
        title: 'Cube | Buy/Sell',
      },
      {
        path: 'portfolio',
        loadComponent: () =>
          import('./pages/portfolio/portfolio.component').then(
            (m) => m.PortfolioComponent
          ),
        data: {},
        title: 'Cube | Portfolio',
      },
      {
        path: 'history',
        loadComponent: () =>
          import('./pages/history/history.component').then(
            (m) => m.HistoryComponent
          ),
        data: { headerTitle: 'PAGES.HISTORY', hideSearch: true },
        title: 'Cube | History',
      },
      {
        path: 'money-moving',
        data: { headerTitle: 'PAGES.MONEY_MOVING', hideSearch: true },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/money-moving/money-moving.component').then(
                (m) => m.MoneyMovingComponent
              ),
            title: 'Cube | Money Moving',
          },
          {
            path: 'add-money',
            loadComponent: () =>
              import('./pages/add-money/add-money.component').then(
                (m) => m.AddMoneyComponent
              ),
            data: { headerTitle: 'PAGES.ADD_MONEY' },
            title: 'Cube | Add Money',
          },
          {
            path: 'withdrawal',
            loadComponent: () =>
              import('./pages/withdrawal/withdrawal.component').then(
                (m) => m.WithdrawalComponent
              ),
            data: { headerTitle: 'PAGES.WITHDRAWAL' },
            title: 'Cube | Withdrawal',
          },
        ],
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings/settings.component').then(
            (m) => m.SettingsComponent
          ),
        data: { headerTitle: 'SETTINGS.ACCOUNT_SETTINGS', hideSearch: true },
        children: [
          { path: '', redirectTo: 'profile', pathMatch: 'full' },
          {
            path: 'profile',
            loadComponent: () =>
              import('./pages/settings/tabs/profile/settings-profile.component').then(
                (m) => m.SettingsProfileComponent
              ),
            title: 'Cube | Settings - Profile',
          },
          {
            path: 'kyc',
            loadComponent: () =>
              import('./pages/settings/tabs/kyc/settings-kyc.component').then(
                (m) => m.SettingsKycComponent
              ),
            title: 'Cube | Settings - KYC',
          },
          {
            path: 'tfa',
            loadComponent: () =>
              import('./pages/settings/tabs/tfa/settings-tfa.component').then(
                (m) => m.SettingsTfaComponent
              ),
            title: 'Cube | Settings - 2FA',
          },
          {
            path: 'notification',
            loadComponent: () =>
              import('./pages/settings/tabs/notification/settings-notification.component').then(
                (m) => m.SettingsNotificationComponent
              ),
            title: 'Cube | Settings - Notification',
          },
          {
            path: 'password',
            loadComponent: () =>
              import('./pages/settings/tabs/password/settings-password.component').then(
                (m) => m.SettingsPasswordComponent
              ),
            title: 'Cube | Settings - Password',
          },
        ],
      },
      {
        path: 'help',
        loadComponent: () =>
          import('./pages/help/help.component').then(
            (m) => m.HelpComponent
          ),
        data: { headerTitle: 'HELP.PAGE_TITLE', hideSearch: true },
        children: [
          { path: '', redirectTo: 'contact-us', pathMatch: 'full' },
          {
            path: 'contact-us',
            loadComponent: () =>
              import('./pages/help/tabs/contact-us/help-contact-us.component').then(
                (m) => m.HelpContactUsComponent
              ),
            title: 'Cube | Help - Contact Us',
          },
          {
            path: 'legal-policies',
            loadComponent: () =>
              import('./pages/help/tabs/legal-policies/help-legal-policies.component').then(
                (m) => m.HelpLegalPoliciesComponent
              ),
            title: 'Cube | Help - Legal & Policies',
          },
        ],
      },
    ],
  },
  {
    path: 'network-error',
    loadComponent: () =>
      import('./pages/network-error/network-error.component').then(
        (m) => m.NetworkErrorComponent
      ),
    title: 'Cube | Network Error',
  },
  {
    path: 'service-unavailable',
    loadComponent: () =>
      import('./pages/service-unavailable/service-unavailable.component').then(
        (m) => m.ServiceUnavailableComponent
      ),
    canActivate: [serviceUnavailableGuard],
    title: 'Cube | Service Unavailable',
  },
  {
    path: '**',
    redirectTo: 'sign-in',
  },
];

export const appRoutes: Route[] = [
  {
    path: ':lang',
    canActivate: [langGuard],
    children: langChildRoutes,
  },
  {
    path: '',
    pathMatch: 'full',
    canActivate: [langRedirectGuard],
    loadComponent: () => import('./pages/home/home.component').then(m => m.HomeComponent),
  },
];
